import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import {
  Button,
  ButtonIcon,
  CloudUploadIcon,
  Input,
  SkeletonInputGrid,
  Toggle,
  TrashIcon,
} from '@rabbit/elements/shared-components';
import {
  FileStorageContext,
  VendableEditorContext,
  useGetSingleVendable,
} from '@rabbit/bizproc/react';
import {
  AddEditVendableFormValuesShape,
  DEFAULT_ACCEPTED_FILE_TYPES,
  DocTypeShapeTypes,
  FileUploaderVariations,
  UploadedFileCategories,
} from '@rabbit/elements/shared-types';
import {
  DTVendable,
  DTVendable_Private,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
  DTCategory_Public,
  DTWarranty_Template,
  FBD_Category_Public,
  UserUploadedDocument,
} from '@rabbit/data/types';
import { t } from 'i18next';
import { SageFileUploader } from '../../upload-wrapper/SageFileUploader';
import Skeleton from 'react-loading-skeleton';
import { v4 as uuid } from 'uuid';
import { AppContext } from '@rabbit/app-context';
import { REGISTRATION_FIELDS } from '@rabbit/config/enums';
import { nestApiGetAllCategories } from '@rabbit/bizproc/core';
import normalizeUrl from 'normalize-url';
import { supagardBrandCategories } from '../../ModalNewEditPartner/ModalNewEditPartner';
import { SerialNumberReference } from './SerialNumberReference';

export interface ModalAddEditVendableProps {
  handleClose: () => void;
  //handleDelete?: () => void;
  vendableId?: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  data?: any;
  onChangeStep?: (step_label: string, data: any) => void;
}

export function ModalAddEditVendableV2({
  handleClose,
  //handleDelete,
  setLoading,
  vendableId,
  data,
  onChangeStep,
}: ModalAddEditVendableProps) {
  const [validateOnChange, setValidateOnChange] = useState<boolean>(false);
  const activePremiumTenant = t('tenantLink');
  const formikRef = useRef<FormikProps<AddEditVendableFormValuesShape> | null>(
    null
  );
  const { config, tenantInfo } = useContext(AppContext) || {};
  const isUniqueProductActivated =
    config.HOLDINGS.NEW_REGISTRATION_FLOW.UNIQUE_PRODUCT;
  const newVendableId = !vendableId
    ? `${activePremiumTenant}_${uuid()}`
    : undefined;

  const contextValues = useContext(VendableEditorContext);
  const {
    createOrUpdateVendable,
    warrantyTemplates,
    warrantyTemplatesLoading,
    warrantyTemplatesError,
    setShouldRefresh,
    getSingleVendable,
    getSingleWarrantyTemplate,
  } = contextValues;
  const {
    unusedCompletedCleanup,
    moveCompletedUploadsToAttached,
    deleteFile,
    uploadQueueState,
  } = useContext(FileStorageContext) || {};

  // Prevent submitting while vendable image is uploading
  const vendableDocId = vendableId ?? newVendableId ?? null;
  const isVendableImageUploading =
    uploadQueueState?.ongoing?.some(
      (u) =>
        u.category === UploadedFileCategories.VendableImages &&
        u.docType?.type === DocTypeShapeTypes.Vendable &&
        u.docType?.docid === vendableDocId
    ) ?? false;

  // Use the new hook to fetch vendable data with real-time updates
  const {
    vendableData,
    loading: vendableLoading,
    error: vendableError,
  } = useGetSingleVendable(vendableId, undefined, activePremiumTenant);

  const [categoryOptions, setCategoryOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [isCategoriesLoading, setIsCategoriesLoading] =
    useState<boolean>(false);
  const [warrantyTemplate, setWarrantyTemplate] =
    useState<DTWarranty_Template | null>(null);
  const [isImageUploaded, setIsImageUploaded] = useState<boolean>(false);
  const [parsedSerialNumbers, setParsedSerialNumbers] = useState<string[]>([]);
  const pendingSubmissionRef = useRef<AddEditVendableFormValuesShape | null>(
    null
  );
  const warrantyPlanOptions = generateWarrantyTemplateOptions(
    warrantyTemplates || []
  );
  // Ensure auto-select when there is exactly one warranty plan option
  const didAutoSelectWarranty = useRef<boolean>(false);

  useEffect(() => {
    const formik = formikRef.current;
    if (!formik) return;
    if (!warrantyPlanOptions || warrantyPlanOptions.length !== 1) return;

    const currentValue = formik.values?.warrantyTemplate;
    const isCurrentValid = warrantyPlanOptions.some(
      (opt) => opt.value === currentValue
    );

    if (!didAutoSelectWarranty.current && (!currentValue || !isCurrentValid)) {
      formik.setFieldValue(
        'warrantyTemplate',
        warrantyPlanOptions[0].value,
        false
      );
      didAutoSelectWarranty.current = true;
    }
  }, [warrantyPlanOptions]);

  const defaultBrandName = t('CFG_COBRAND_NAME', '') ?? '';

  /* ------------------------------- Handle vendable data ------------------------------- */
  // Seeded once per product on purpose. This can't share the effect below:
  // getSingleWarrantyTemplate is rebuilt on every provider render, so that effect
  // re-runs constantly and kept resetting the flag to false in between the user
  // picking an image and hitting save, which saved the product without its image.
  // undefined, not null, so an unsaved vendable (null docid) still seeds once
  const imageFlagSeededFor = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!vendableData || imageFlagSeededFor.current === vendableDocId) return;
    imageFlagSeededFor.current = vendableDocId;
    setIsImageUploaded(!!vendableData?.pub?.images?.length);
  }, [vendableData, vendableDocId]);

  useEffect(() => {
    if (vendableData) {
      // Parse CSV file if it exists
      if (data?.pub?.serialNumbersCsv) {
        parseCSVFile(data.pub.serialNumbersCsv);
      }
      getSingleWarrantyTemplate(vendableData?.pub?.defaultTemplate ?? '').then(
        (data) => {
          if (data) {
            setWarrantyTemplate(data);
          }
        }
      );
    }
  }, [vendableData, getSingleWarrantyTemplate]);

  /* -------------------- Clean up unused completed uploads ------------------- */
  useEffect(() => {
    return () => {
      if (unusedCompletedCleanup) {
        void unusedCompletedCleanup();
      }
    };
  }, []);

  useEffect(() => {
    if (pendingSubmissionRef.current) {
      const values = pendingSubmissionRef.current;
      pendingSubmissionRef.current = null; // Clear pending reference
      onSubmit(values); // Proceed with submission
    }
  }, [uploadQueueState]);

  // Function to parse CSV file
  const parseCSVFile = useCallback(
    async (csvFile: UserUploadedDocument) => {
      try {
        const response = await fetch(csvFile.url);
        const text = await response.text();

        // Split by newline and filter out empty lines
        const serialNumbers = text
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.length > 0);

        setParsedSerialNumbers(serialNumbers);
      } catch (error) {
        console.error('Error parsing CSV file:', error);
        toast.error(t('message.errorParsingCsvFile'));
      }
    },
    [t]
  );

  // Watch for changes to the serialNumbersCsv field
  useEffect(() => {
    const formikInstance = formikRef.current;
    if (formikInstance?.values.serialNumbersCsv) {
      parseCSVFile(formikInstance.values.serialNumbersCsv);
    } else {
      setParsedSerialNumbers([]);
    }
  }, [uploadQueueState, parseCSVFile]);

  useEffect(() => {
    let mounted = true;
    setIsCategoriesLoading(true);
    (async () => {
      try {
        const tenant = t('tenantLink');
        const docs = await FBD_Category_Public.query()
          .where('tenantLink', '==', tenant)
          .get();
        if (!mounted) return;
        const options =
          docs
            .map((r) => r.body as DTCategory_Public)
            .filter(Boolean)
            .map((c) => ({ label: c?.titles?.en || c.key, value: c.key })) ??
          [];
        setCategoryOptions(options);
      } catch (e) {
        console.error('Failed to fetch categories', e);
        if (mounted) setCategoryOptions([]);
      } finally {
        if (mounted) setIsCategoriesLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [t]);

  // Normalize initial category values (labels vs keys) once options are loaded
  useEffect(() => {
    const formikInstance = formikRef.current;
    if (!formikInstance) return;
    if (!categoryOptions?.length) return;
    // Source categories could be labels (saved previously) or keys
    const source =
      (vendableData?.pub?.category as
        | Array<string | { label: string; value: string }>
        | undefined) ??
      (formikInstance.values.category as
        | Array<string | { label: string; value: string }>
        | undefined);
    if (!Array.isArray(source) || source.length === 0) return;
    const normalized = source
      .map((cat) => {
        if (typeof cat === 'string') {
          // If already a key, keep; else map label -> key
          const byValue = categoryOptions.find((o) => o.value === cat)?.value;
          if (byValue) return byValue;
          const byLabel = categoryOptions.find((o) => o.label === cat)?.value;
          return byLabel ?? cat;
        }
        return cat?.value ?? cat?.label ?? '';
      })
      .filter(Boolean);
    // Only update if different to avoid loops
    const current = formikInstance.values.category as any[];
    const isSame =
      Array.isArray(current) &&
      current.length === normalized.length &&
      current.every((v: any, i: number) => v === normalized[i]);
    if (!isSame) {
      void formikInstance.setFieldValue('category', normalized);
    }
  }, [categoryOptions, vendableData?.pub?.category]);

  /* -------------------------------------------------------------------------- */
  /*                              Handle submission                             */
  /* -------------------------------------------------------------------------- */
  const onSubmit = async (values: AddEditVendableFormValuesShape) => {
    setLoading(true);
    if (uploadQueueState?.ongoing.length) {
      pendingSubmissionRef.current = values; // Queue the submission
      return;
    }

    // Only process serial number fields if the feature is enabled and the checkbox is checked
    if (
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
      values.enableSerialNumberValidation
    ) {
      // Ensure serialNumbers is an array
      if (!values.serialNumbers) {
        values.serialNumbers = [];
      }

      // Ensure serialNumberRanges is an array and has Symbol.iterator
      if (!values.serialNumberRanges) {
        values.serialNumberRanges = [];
      }

      // Add Symbol.iterator method to ensure it's iterable
      if (
        values.serialNumberRanges &&
        !values.serialNumberRanges[Symbol.iterator]
      ) {
        Object.defineProperty(values.serialNumberRanges, Symbol.iterator, {
          enumerable: false,
          configurable: true,
          writable: true,
          value: Array.prototype[Symbol.iterator],
        });
      }
    } else if (
      config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
      !values.enableSerialNumberValidation
    ) {
      // If the feature is enabled but the checkbox is unchecked, clear the serial number fields
      values.serialNumbers = [];
      values.serialNumberRanges = [];
      values.serialNumbersCsv = undefined;
    }

    let uploadedFiles = values.vendableImages;
    if (uploadQueueState?.completed.length)
      uploadQueueState?.completed.forEach((file) => {
        if (
          file.category === UploadedFileCategories.VendableImages &&
          file.uploadedFile
        ) {
          uploadedFiles = [file.uploadedFile];
        }
      });
    // The flag is only set by the upload button, so fall back to the form field
    // to catch any other way a file lands there. Both are cleared on remove.
    const hasImage = isImageUploaded || (values.vendableImages?.length ?? 0) > 0;

    const pubVendable: Partial<DTVendable> = {
      title: values.title,
      // We'll not be doing any differentiation between title and full title for now
      full: values.title,
      detail: values.detail,
      brand: values.brand,
      img: hasImage ? uploadedFiles?.map((image) => image?.url as string) : [],
      images: hasImage ? uploadedFiles : [],
      upc: values.upc,
      mpn: values.sku,
      unique: isUniqueProductActivated ? values.unique : false,
      mfr:
        PersonaTypeSingleLetter.Manufacturer +
        PersonaIdTypeSplitter +
        activePremiumTenant,
      // Save category as human-readable labels (not keys)
      category: Array.isArray(values.category)
        ? values.category.map((cat) => {
            if (typeof cat === 'string') {
              const found = categoryOptions.find((opt) => opt.value === cat);
              return found?.label ?? cat;
            }
            return (cat as any)?.label ?? (cat as any)?.value ?? '';
          })
        : [],
      keywords: values.keywords,
      defaultTemplate: values.warrantyTemplate,
      deprecated: values.deprecated === 'true' ? true : false,
      manual: values.manual,
      serialNumberHelp: {
        text: values.serialNumberHelp?.text ?? '',
        image: Array.isArray(values.serialNumberHelp?.image)
          ? (values.serialNumberHelp?.image as any)
          : values.serialNumberHelp?.image
          ? [values.serialNumberHelp.image as any]
          : [],
        link: values.serialNumberHelp?.link ?? '',
        video: values.serialNumberHelp?.video || undefined,
      },
      published: values.published,
      ...(config.VENDABLES.CUSTOM_FIELDS[
        REGISTRATION_FIELDS.CHASSIS_NUMBER
      ] && { chassis_number: values.chassis_number }),
      ...(config.VENDABLES.CUSTOM_FIELDS[
        REGISTRATION_FIELDS.REGISTRATION_NUMBER
      ] && { registration_number: values.registration_number ?? '' }),
      ...(config.VENDABLES.CUSTOM_FIELDS[
        REGISTRATION_FIELDS.PRODUCTION_DATE
      ] && { production_date: values.production_date }),
      ...(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
      values.enableSerialNumberValidation
        ? {
            serialNumbers: values.serialNumbers,
            serialNumberRanges: values.serialNumberRanges,
            serialNumbersCsv: values.serialNumbersCsv,
            serialNumberEnabled: true,
          }
        : {
            serialNumbers: [],
            serialNumberRanges: [],
            serialNumbersCsv: undefined,
            serialNumberEnabled: false,
          }),
    };
    const rrp = {
      amount: Number(values.rrp?.amount) ?? 0,
      currency: values.rrp?.currency ?? 'AUD',
    };

    const privVendable: Partial<DTVendable_Private> = {
      mfr:
        PersonaTypeSingleLetter.Manufacturer +
        PersonaIdTypeSplitter +
        activePremiumTenant,
      ...(config.VENDABLES.CUSTOM_FIELDS.RRP && {
        rrp: rrp,
      }),
      published: values.published,
      deprecated: values.deprecated === 'true' ? true : false,
    };
    try {
      const payload = {
        publicData: pubVendable,
        privateData: privVendable,
        vendableId,
        newVendableId,
      };
      const docId = await createOrUpdateVendable(payload);
      if (!onChangeStep) {
        setShouldRefresh(6000);
      }

      moveCompletedUploadsToAttached?.(uploadQueueState?.completed ?? []);
      // delete all attached vendable image files that aren't the new one.
      // (manual can have multiple)
      // This is to prevent orphaned files in the file storage
      // when we enable multiple images per vendable this needs to be reviewed
      if (vendableData?.pub.images && values.vendableImages.length > 0) {
        const filesToBeCleared = vendableData.pub.images.filter(
          (file) => file.url !== values.vendableImages[0].url
        );
        if (filesToBeCleared.length > 0 && deleteFile) {
          for (const file of filesToBeCleared) {
            void deleteFile(file.url, {
              category: UploadedFileCategories.VendableImages,
            });
          }
        }
      }
      toast.success(t('message.productRegisteredSuccessfully'));
      if (!onChangeStep) {
        handleClose();
      }
      if (onChangeStep) {
        onChangeStep('add-product', {
          ...data,
          productResponse: {
            id: docId,
            label: values.title + ' (' + values.sku + ')',
            value: docId,
          },
        });
      }
    } catch (err) {
      console.error(err);
      toast.error(
        `${t('Something went wrong while')} ${
          newVendableId ? t('creating') : t('editing')
        } ${t('the product! Please try again later.')}`
      );
    } finally {
      pendingSubmissionRef.current = null;
      setLoading(false);
    }
  };
  /* -------------------------------------------------------------------------- */
  /*                                 Form setup                                 */
  /* -------------------------------------------------------------------------- */

  // These are the fields we will be going with for the editor. For non-Shopify based tenants,
  // they will all be editable. For Shopify based ones, however, some will be read-only, and
  // some will be editable

  const schema = Yup.object().shape({
    title: Yup.string().required(t('message.pleaseEnterATitle')),
    detail: Yup.string(),
    brand: Yup.string().required(t('message.pleaseEnterABrand')),
    ...(config.VENDABLES.MANDATORY_FIELDS.UPC
      ? { upc: Yup.string().required(t('message.pleaseEnterUPC')) }
      : { upc: Yup.string() }),
    sku: Yup.string().required(t('Required')),
    category: Yup.array().min(1).required(t('message.pleaseSelectACategory')),
    // disabled for first release -dc
    // keywords: Yup.array(),
    warrantyTemplate: Yup.string().required(t('Required')),
    ...(config.VENDABLES.CUSTOM_FIELDS.RRP && {
      rrp: Yup.object({
        amount: Yup.number().required(t('Required')),
        currency: Yup.string().required(t('Required')),
      }),
    }),
    manual: Yup.array(),
    // todo type this to UserUploadedDocument[] -dc
    vendableImages: Yup.array(),
    published: Yup.boolean().required(t('Required')),
    unique: Yup.boolean().default(false),
    // this is later forced into boolean, but has to be string to due to some weird input behaviours
    ...(config.VENDABLES.CUSTOM_FIELDS.DEPRECATED && {
      deprecated: Yup.string().required(t('Required')),
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.CHASSIS_NUMBER] && {
      chassis_number: Yup.string().required(t('Required')),
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[
      REGISTRATION_FIELDS.REGISTRATION_NUMBER
    ] && {
      registration_number: Yup.string().required(t('Required')),
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.PRODUCTION_DATE] && {
      production_date: Yup.date().required(t('Required')),
    }),
    serialNumberHelp: Yup.object({
      text: Yup.string().optional(),
      image: Yup.array().optional(),
      link: Yup.string()
        .transform((val) => {
          if (!val) return val;
          try {
            return normalizeUrl(val, { defaultProtocol: 'https:' });
          } catch {
            return val;
          }
        })
        .url()
        .optional(),
      video: Yup.object({
        url: Yup.string().url().optional(),
      }).optional(),
    }).optional(),
    serialNumberRequired: Yup.boolean(),
    ...(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED && {
      serialNumbers: Yup.array().of(Yup.string()),
      serialNumberRanges: Yup.array().of(
        Yup.object().shape({
          start: Yup.string().required(t('Required')),
          end: Yup.string().required(t('Required')),
        })
      ),
      serialNumbersCsv: Yup.object(),
      // Temporary fields for UI interaction
      newSerialNumber: Yup.string(),
      newRangeStart: Yup.string(),
      newRangeEnd: Yup.string(),
      // Toggle for serial number validation
      enableSerialNumberValidation: Yup.boolean(),
    }),
  });

  const initialValues: AddEditVendableFormValuesShape = {
    title: vendableData?.pub.title ?? '',
    brand: vendableData?.pub?.brand ?? defaultBrandName,
    detail: vendableData?.pub?.detail ?? '',
    upc: vendableData?.pub?.upc ?? '',
    sku: vendableData?.pub?.mpn ?? '',
    category: vendableData?.pub.category ?? [],
    keywords: vendableData?.pub.keywords ?? [],
    warrantyTemplate: vendableData?.pub.defaultTemplate ?? '',
    ...(config.VENDABLES.CUSTOM_FIELDS.RRP
      ? {
          rrp: vendableData?.priv.rrp ?? {
            amount: 0,
            currency: tenantInfo?.currency ?? 'GBP',
          },
        }
      : {}),
    manual: vendableData?.pub.manual ?? [],
    vendableImages: vendableData?.pub.images ?? [],
    published: vendableData?.pub.published ?? false,
    unique: vendableData?.pub.unique
      ? vendableData?.pub.unique
      : onChangeStep
      ? true
      : false,
    ...(config.VENDABLES.CUSTOM_FIELDS.DEPRECATED
      ? {
          deprecated: vendableData?.pub.deprecated ? 'true' : 'false',
        }
      : {}),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.CHASSIS_NUMBER] && {
      chassis_number: vendableData?.pub.chassis_number ?? '',
    }),
    ...(config.VENDABLES.CUSTOM_FIELDS[
      REGISTRATION_FIELDS.REGISTRATION_NUMBER
    ] && { registration_number: vendableData?.pub.registration_number ?? '' }),
    ...(config.VENDABLES.CUSTOM_FIELDS[REGISTRATION_FIELDS.PRODUCTION_DATE] && {
      production_date: vendableData?.pub.production_date
        ? new Date(vendableData?.pub.production_date!)
        : new Date(),
    }),
    serialNumberRequired:
      vendableData?.pub.serialNumberRequired ??
      config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED,
    serialNumberHelp: {
      text: vendableData?.pub.serialNumberHelp?.text ?? '',
      image: (vendableData?.pub.serialNumberHelp?.image as any) ?? [],
      link: vendableData?.pub.serialNumberHelp?.link ?? '',
      video: vendableData?.pub.serialNumberHelp?.video,
    },
    ...(config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED && {
      serialNumbers: vendableData?.pub.serialNumbers ?? [],
      // Initialize serialNumberRanges and ensure it has Symbol.iterator
      serialNumberRanges: (() => {
        const ranges = vendableData?.pub.serialNumberRanges ?? [];
        if (!ranges[Symbol.iterator]) {
          Object.defineProperty(ranges, Symbol.iterator, {
            enumerable: false,
            configurable: true,
            writable: true,
            value: Array.prototype[Symbol.iterator],
          });
        }
        return ranges;
      })(),
      serialNumbersCsv: vendableData?.pub.serialNumbersCsv ?? undefined,
      // Temporary fields for UI interaction
      newSerialNumber: '',
      newRangeStart: '',
      newRangeEnd: '',
      // Toggle for serial number validation
      enableSerialNumberValidation:
        (vendableData?.pub.serialNumbers &&
          vendableData?.pub.serialNumbers?.length > 0) ||
        (vendableData?.pub.serialNumberRanges &&
          vendableData?.pub.serialNumberRanges?.length > 0) ||
        !!vendableData?.pub.serialNumbersCsv,
    }),
  };

  // Ref controls for the file uploader input
  const selectFileRef = useRef<HTMLElement>();
  const removeFileRef = useRef<HTMLElement>();

  /* -------------------------------------------------------------------------- */
  /*                                     TSX                                    */
  /* -------------------------------------------------------------------------- */
  if (!newVendableId && !vendableData)
    return (
      <div className="p-5">
        {/** This is pretty unique to this modal, but if used again extract to a separate component */}
        <div className="mb-5 grid grid-cols-4 gap-2 rounded-md border border-gray-200 p-2">
          <div className="col-span-1">
            <Skeleton count={1} className="py-14" />
          </div>
          <div className="col-span-3 py-3 ">
            <div className="mb-4 grid grid-cols-2 gap-2">
              <Skeleton count={1} className="py-2" />
              <Skeleton count={1} className="!w-1/2 py-2" />
            </div>
            <Skeleton count={1} className="!w-1/3 py-2" />
          </div>
          <div className="col-span-4">
            <Skeleton count={1} className="py-3" />
          </div>
        </div>
        <SkeletonInputGrid cols={2} rows={5} />
      </div>
    );

  const getCategories = () => {
    // Return empty array if categories haven't loaded yet or feature is disabled
    if (!config.CATEGORY.ENABLED || !categoryOptions) {
      return tenantInfo?.shortName?.toLowerCase() === 'transporter'
        ? TransporterCategories
        : [];
    }
    return categoryOptions;
  };
  return (
    <div>
      <Formik
        innerRef={formikRef}
        validationSchema={schema}
        initialValues={initialValues}
        enableReinitialize={
          !config.HOLDINGS.NEW_REGISTRATION_FLOW.ADD_NEW_PRODUCT
        }
        onSubmit={onSubmit}
        validateOnChange={validateOnChange}
        validateOnBlur={false}
      >
        {(props) => {
          return (
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (isVendableImageUploading) {
                  toast.info(t('Please wait until the image upload finishes.'));
                  return;
                }
                props.validateForm();
                setValidateOnChange(true);
                props.handleSubmit(e);
              }}
            >
              <div className="flex flex-col gap-4 px-4 pt-4">
                <div className="flex flex-col gap-4 rounded-lg border border-gray-200 p-2">
                  <div className="flex justify-start gap-4">
                    <div>
                      <SageFileUploader
                        variation={FileUploaderVariations.InputPicture}
                        name={'vendableImages'}
                        identifiers={{
                          personaId:
                            PersonaTypeSingleLetter.Manufacturer +
                            PersonaIdTypeSplitter +
                            activePremiumTenant,
                          category: UploadedFileCategories.VendableImages,
                          docType: {
                            type: DocTypeShapeTypes.Vendable,
                            docid: vendableId ?? newVendableId ?? null,
                          },
                        }}
                        currentFiles={props.values.vendableImages ?? []}
                        refs={{ selectFileRef, removeFileRef }}
                        maxFiles={1}
                        accepts={DEFAULT_ACCEPTED_FILE_TYPES}
                      />
                    </div>
                    <div className="flex flex-col gap-4 py-4">
                      <div className="flex gap-2">
                        <div className="text-lg font-bold">
                          {vendableData?.pub.title ?? 'Product name'}
                        </div>
                        <div>
                          {vendableData?.pub.published ? (
                            <div className="inline overflow-hidden rounded bg-green-100 py-1 px-2">
                              {t('Published')}
                            </div>
                          ) : (
                            <div className="inline overflow-hidden rounded bg-gray-100 py-1 px-2">
                              {t('Unpublished')}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        {props.values.vendableImages.length === 0 && (
                          <ButtonIcon
                            kind="bgLightGreen"
                            onClick={() => {
                              selectFileRef.current?.click();
                              setIsImageUploaded(true);
                            }}
                            label={t('Upload image')}
                            Icon={CloudUploadIcon}
                            iconLeft
                          />
                        )}
                        {props.values.vendableImages.length > 0 && (
                          <ButtonIcon
                            kind="bgRed"
                            onClick={() => {
                              removeFileRef.current?.click();
                              setIsImageUploaded(false);
                            }}
                            label={t('Remove image')}
                            Icon={TrashIcon}
                            iconLeft
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                    <div className="grow">{t('Publish product')}</div>
                    <Toggle
                      name="publish"
                      checked={props.values.published}
                      onChange={() => {
                        props.setFieldValue(
                          'published',
                          !props.values.published
                        );
                      }}
                    />
                  </div>
                  {isUniqueProductActivated && (
                    <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                      <div className="grow">{t('Unique product')}</div>
                      <Toggle
                        name="unique"
                        checked={props.values.unique || false}
                        onChange={() => {
                          props.setFieldValue('unique', !props.values.unique);
                        }}
                        disabled={!!onChangeStep}
                      />
                    </div>
                  )}
                  {config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_REQUIRED && (
                    <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                      <div className="grow">{t('Serial number required')}</div>
                      <Toggle
                        name="serialNumberRequired"
                        checked={props.values.serialNumberRequired || false}
                        onChange={() => {
                          props.setFieldValue(
                            'serialNumberRequired',
                            !props.values.serialNumberRequired
                          );
                        }}
                      />
                    </div>
                  )}
                  {config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED && (
                    <div className="flex w-full items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 px-3 py-2">
                      <div className="grow">{t('general.serialNumbers')}</div>
                      <Toggle
                        name="enableSerialNumberValidation"
                        checked={
                          props.values.enableSerialNumberValidation || false
                        }
                        onChange={() => {
                          props.setFieldValue(
                            'enableSerialNumberValidation',
                            !props.values.enableSerialNumberValidation
                          );
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      type="text"
                      label={t('Product name') + '*'}
                      name="title"
                      settings={{
                        hint: '*' + t('required'),
                        placeholder: t('Product name'),
                        allowSpecialCharacter: true,
                        allowedSpecialCharacters: [`'`, `.`, '-'],
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    {config.VENDABLES.BRAND_SELECTABLE ? (
                      <Input
                        type="select"
                        label={t('Brand') + '*'}
                        name="brand"
                        settings={{
                          options: supagardBrandCategories,
                          placeholder: t('general.pleaseSelectABrand'),
                          id: 'brand',
                          errors: props.errors,
                          hint: '*' + t('required'),
                        }}
                      />
                    ) : (
                      <Input
                        type="text"
                        label={t('Brand') + '*'}
                        name="brand"
                        settings={{
                          hint: '*' + t('required'),
                          placeholder: t('Brand name'),
                          allowSpecialCharacter: true,
                          allowedSpecialCharacters: [`'`],
                        }}
                      />
                    )}
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    {/* Meanwhile, we use this one */}
                    <Input
                      type="select"
                      label={t('Category') + '*'}
                      name="category"
                      settings={{
                        isMulti: true,
                        options: getCategories(),
                        placeholder: isCategoriesLoading
                          ? t('Loading categories...')
                          : t('general.pleaseSelectACategory'),
                        id: 'category',
                        errors: props.errors,
                        hint: '*' + t('required'),
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="text"
                      label={t('SKU') + '*'}
                      name="sku"
                      settings={{
                        hint: '*' + t('required'),
                        placeholder: t('SKU'),
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-1">
                    <Input
                      type="text"
                      label={
                        t('UPC') +
                        `${config.VENDABLES.MANDATORY_FIELDS.UPC ? '*' : ''}`
                      }
                      name="upc"
                      settings={{
                        hint: config.VENDABLES.MANDATORY_FIELDS.UPC
                          ? '*' + t('required')
                          : '',
                        placeholder: t('UPC'),
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <Input
                      type="select"
                      label={t(`${config.PRINT_SUMMARY.TITLE} plan`) + '*'}
                      name="warrantyTemplate"
                      settings={{
                        options: warrantyPlanOptions,
                        placeholder: t('message.pleaseSelectAnOption'),
                        id: 'warranty_plan',
                        errors: props.errors,
                      }}
                    />
                  </div>
                </div>
                {(config.VENDABLES.CUSTOM_FIELDS[
                  REGISTRATION_FIELDS.CHASSIS_NUMBER
                ] ||
                  config.VENDABLES.CUSTOM_FIELDS[
                    REGISTRATION_FIELDS.REGISTRATION_NUMBER
                  ]) && (
                  <div className="flex gap-4">
                    {config.VENDABLES.CUSTOM_FIELDS[
                      REGISTRATION_FIELDS.CHASSIS_NUMBER
                    ] && (
                      <div className="flex-1">
                        <Input
                          type="text"
                          label={`${t('general.chassis_number')}*`}
                          name="chassis_number"
                          settings={{
                            id: 'chassis_number',
                            placeholder: 'Enter chassis number',
                          }}
                        />
                      </div>
                    )}
                    {config.VENDABLES.CUSTOM_FIELDS[
                      REGISTRATION_FIELDS.REGISTRATION_NUMBER
                    ] && (
                      <div className="flex-1">
                        <Input
                          type="text"
                          label={`${t('general.registration_number')}*`}
                          name="registration_number"
                          settings={{
                            id: 'registration_number',
                            placeholder: 'Enter registration number',
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-4">
                  {config.VENDABLES.CUSTOM_FIELDS[
                    REGISTRATION_FIELDS.PRODUCTION_DATE
                  ] && (
                    <div className="flex-1">
                      <Input
                        type="datepicker"
                        name="production_date"
                        label={`${t('general.production_date')}*`}
                        settings={{
                          id: 'production_date',
                          placeholder: 'DD/MM/YYYY',
                        }}
                      />
                    </div>
                  )}
                  {config.VENDABLES.CUSTOM_FIELDS.RRP && (
                    <div className="flex-1">
                      <Input
                        type="currency"
                        label={t('general.rrp')}
                        name="rrp"
                        settings={{
                          id: 'rrp',
                          currency:
                            props?.values?.rrp?.currency ??
                            tenantInfo?.currency,
                        }}
                      />
                    </div>
                  )}
                  {config.VENDABLES.CUSTOM_FIELDS.DEPRECATED && (
                    <div className="flex-1">
                      <Input
                        type="select"
                        label={t('Deprecated')}
                        name="deprecated"
                        settings={{
                          // For some reason I can never assign booleans or falsy values here.
                          // They'll turn into an empty string instead of false, or 0, or whatever
                          // So I'm using "true" and "false" instead.
                          options: [
                            { value: 'true', label: t('Yes') },
                            { value: 'false', label: t('No') },
                          ],
                          placeholder: t('Deprecated'),
                          id: 'deprecated',
                          errors: props.errors,
                        }}
                      />
                    </div>
                  )}
                </div>
                <div className="">
                  <Input
                    type="rich-text"
                    label={t('Product description')}
                    name="detail"
                    settings={{
                      shouldValidate: false,
                    }}
                  />
                </div>
                <SageFileUploader
                  variation={FileUploaderVariations.FormItem}
                  name={'manual'}
                  label={t('Product documentation')}
                  identifiers={{
                    personaId:
                      PersonaTypeSingleLetter.Manufacturer +
                      PersonaIdTypeSplitter +
                      activePremiumTenant,
                    category: UploadedFileCategories.VendableManuals,
                    docType: {
                      type: DocTypeShapeTypes.Vendable,
                      docid: vendableId ?? newVendableId ?? null,
                    },
                  }}
                  currentFiles={props.values.manual ?? []}
                  maxFiles={5}
                  accepts={DEFAULT_ACCEPTED_FILE_TYPES}
                />
                {config.HOLDINGS.REGISTRATION.SERIAL_NUMBER_HELP.ENABLED && (
                  <div className="mt-2 rounded-lg border border-gray-200 p-2">
                    <div className="mb-2 text-base font-semibold">
                      {t('Where to find the serial number')}
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          type="text"
                          label={t('Serial number help text')}
                          name="serialNumberHelp.text"
                          settings={{
                            placeholder: t(
                              'Describe where to find the serial number'
                            ),
                            allowSpecialCharacter: true,
                          }}
                        />
                      </div>
                    </div>
                    <SageFileUploader
                      variation={FileUploaderVariations.FormItem}
                      name={'serialNumberHelp.image'}
                      label={t('Serial number help image')}
                      identifiers={{
                        personaId:
                          PersonaTypeSingleLetter.Manufacturer +
                          PersonaIdTypeSplitter +
                          activePremiumTenant,
                        category: UploadedFileCategories.SerialNumberHelpImage,
                        docType: {
                          type: DocTypeShapeTypes.Vendable,
                          docid: vendableId ?? newVendableId ?? null,
                        },
                      }}
                      currentFiles={
                        Array.isArray(props.values.serialNumberHelp?.image)
                          ? (props.values.serialNumberHelp?.image as any)
                          : props.values.serialNumberHelp?.image
                          ? [props.values.serialNumberHelp.image as any]
                          : []
                      }
                      maxFiles={2}
                      accepts={DEFAULT_ACCEPTED_FILE_TYPES}
                    />
                    <div className="flex gap-4">
                      <div className="flex-1">
                        <Input
                          type="text"
                          label={t('Serial number help link')}
                          name="serialNumberHelp.link"
                          settings={{
                            placeholder: 'https://example.com/video-link',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {config.HOLDINGS.SERIAL_NUMBER_VALIDATION.ENABLED &&
                  props.values.enableSerialNumberValidation && (
                    <SerialNumberReference
                      props={props}
                      vendableId={vendableId}
                      newVendableId={newVendableId}
                      activePremiumTenant={activePremiumTenant}
                      parsedSerialNumbers={parsedSerialNumbers}
                    />
                  )}
                <div className="sticky bottom-0 bg-white py-2">
                  <Button
                    kind="primary"
                    type="submit"
                    children={t('Save')}
                    disabled={
                      !props.isValid || !props.dirty || isVendableImageUploading
                    }
                    className="w-full"
                  />
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

function generateWarrantyTemplateOptions(
  warrantyTemplates: DTWarranty_Template[]
) {
  return warrantyTemplates.map((template) => ({
    id: template.orderNo,
    value: template.docid,
    label: template.title,
  }));
}

const TransporterCategories = [
  { label: 'Axle & Suspension', value: 'axle-suspension' },
  { label: 'EVO8', value: 'EVO8' },
  { label: 'Odyssey Range', value: 'odyssey' },
  { label: 'Voyager Range', value: 'voyager-range' },
  { label: 'Urban Rigid Range', value: 'urban-rigid-range' },
  { label: 'Juno Range', value: 'juno-range' },
  { label: 'Factory Rebuild', value: 'factory-rebuild' },
];
